// SPDX-License-Identifier: GPL-3.0-or-later
// The level select of the start dialog. The player picks the stage that a game starts at. There is
// one button for each stage. A stage above the unlocked stage is disabled, and its text says how to
// open it. The choice lasts for the page only. It is never stored.
// A featured turn always starts at stage 1. The control is hidden in a featured turn, so the start
// of a featured turn never depends on the choice.
// The buttons are native buttons. Keys, mouse and touch work with them. The text goes in with
// `textContent` only.

import { STAGES } from "../src/table/run/stages.ts";
import type { RunSave } from "../src/table/run/types.ts";
import { levelLine } from "./run-text.ts";

/** The stage number that a new page starts at, and that a featured turn always uses. */
const FIRST_STAGE = 1;

/** The part of a button that the level select uses. */
export interface LevelButton {
  type: string;
  textContent: string | null;
  disabled: boolean;
  setAttribute(name: string, value: string): void;
  addEventListener(type: "click", listener: () => void): void;
}

/**
 * The container of the level select. It is the `data-slot="level-select"` element of the start
 * dialog (section 9.4). The page passes it in.
 */
export interface LevelHost<B extends LevelButton = LevelButton> {
  hidden: boolean;
  readonly ownerDocument: { createElement(tag: "button"): B };
  replaceChildren(...nodes: B[]): void;
}

/** The level select. */
export interface LevelSelect {
  /** Show the save state. A featured turn hides the control. */
  render(save: RunSave, featured: boolean): void;
  /** The 1-based stage that a game starts at. A featured turn always gives `FIRST_STAGE`. */
  startStage(featured: boolean): number;
}

/** One stage button with its stage number. */
interface Entry<B extends LevelButton> {
  readonly number: number;
  readonly button: B;
}

/** Make the level select in `host`. It makes one button per stage, in play order. */
export function createLevelSelect<B extends LevelButton>(host: LevelHost<B>): LevelSelect {
  let chosen = FIRST_STAGE;
  let unlocked = FIRST_STAGE;
  let bests: readonly number[] = [];

  /** Show the current state on every button. The buttons stay the same nodes, so focus stays. */
  function paint(): void {
    for (const entry of entries) {
      const def = STAGES[entry.number - 1];
      if (def === undefined) continue;
      entry.button.textContent = levelLine(def, bests[entry.number - 1] ?? 0, unlocked);
      entry.button.disabled = entry.number > unlocked;
      entry.button.setAttribute("aria-pressed", String(entry.number === chosen));
    }
  }

  /** Choose a stage. A locked stage never gets chosen. */
  function choose(number: number): void {
    if (number > unlocked) return;
    chosen = number;
    paint();
  }

  const entries: Entry<B>[] = STAGES.map((def) => {
    const button = host.ownerDocument.createElement("button");
    button.type = "button";
    button.addEventListener("click", () => choose(def.number));
    return { number: def.number, button };
  });
  host.replaceChildren(...entries.map((entry) => entry.button));

  return {
    render(save, featured) {
      unlocked = save.unlocked;
      bests = save.stageBest;
      chosen = Math.max(FIRST_STAGE, Math.min(chosen, unlocked));
      host.hidden = featured;
      paint();
    },
    startStage(featured) {
      return featured ? FIRST_STAGE : chosen;
    },
  };
}
